import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, ChartLine, Settings2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { GridLayoutCore } from "@/components/dashboard/GridLayout";
import { DashboardFilterProvider, useDashboardFilter } from "@/contexts/DashboardFilterContext";
import { ActiveFilterBar } from "@/components/dashboard/GridLayout";
import { DateRangeFilter } from "@/components/dashboard/DateRangeFilter";
import { loadAnalystOverride, saveAnalystOverride } from "@/lib/dashboardStore";
import { useSectionDashboard } from "@/hooks/useSectionDashboard";
import { DashboardEditorPanel } from "@/components/dashboard/DashboardEditorPanel";
import { useSidePanel } from "@/contexts/SidePanelContext";
import { placementKey } from "@/types/widget";
import type { WidgetPlacement } from "@/types/widget";
import type { AnalystOverride } from "@/types/dashboard";

function GlobalDateFilter() {
  const { dateShortcut, customDateRange, onShortcutChange, onCustomRangeChange } =
    useDashboardFilter();
  return (
    <DateRangeFilter
      shortcut={dateShortcut}
      customRange={customDateRange}
      onShortcutChange={onShortcutChange}
      onCustomRangeChange={onCustomRangeChange}
    />
  );
}

export default function MonitoringDashboardView() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const { close: closeSidePanel } = useSidePanel();

  const {
    config,
    isLoading,
    sortedSections,
    save,
    resetUserSections,
    addSection,
    updateSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
    updateSectionPlacements,
    addWidgetToSection,
    renameWidget,
    duplicateWidget,
    removeWidget,
    moveWidgetToSection,
  } = useSectionDashboard(slug ?? "", "user");

  const [override, setOverride] = useState<AnalystOverride>(() =>
    config
      ? loadAnalystOverride(config.id)
      : { dashboardId: "", hiddenInstanceIds: [], sectionLayouts: {} },
  );

  function mutateOverride(updater: (prev: AnalystOverride) => AnalystOverride) {
    setOverride((prev) => {
      const next = updater(prev);
      saveAnalystOverride(next);
      return next;
    });
  }

  function toggleHideWidget(instanceId: string) {
    mutateOverride((prev) => ({
      ...prev,
      hiddenInstanceIds: prev.hiddenInstanceIds.includes(instanceId)
        ? prev.hiddenInstanceIds.filter((id) => id !== instanceId)
        : [...prev.hiddenInstanceIds, instanceId],
    }));
  }

  function updateSectionLayout(sectionId: string, placements: WidgetPlacement[]) {
    mutateOverride((prev) => ({
      ...prev,
      sectionLayouts: { ...prev.sectionLayouts, [sectionId]: placements },
    }));
  }

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const init = new Set<string>();
    config?.sections.forEach((s) => {
      if (s.collapsed) init.add(s.id);
    });
    return init;
  });

  function toggleCollapse(sectionId: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  if (!config) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        {isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <>
            <ChartLine className="h-10 w-10 text-muted/30" />
            <p className="text-sm text-muted">Dashboard not found.</p>
            <Button variant="outline" size="sm" onClick={() => navigate("/monitoring")}>
              Back to Monitoring
            </Button>
          </>
        )}
      </div>
    );
  }

  const hiddenCount = override.hiddenInstanceIds.length;

  if (editMode) {
    return (
      <DashboardEditorPanel
        config={config}
        sortedSections={sortedSections}
        onSave={() => save(config)}
        addSection={addSection}
        updateSection={updateSection}
        deleteSection={deleteSection}
        moveSectionUp={moveSectionUp}
        moveSectionDown={moveSectionDown}
        updateSectionPlacements={updateSectionPlacements}
        addWidgetToSection={addWidgetToSection}
        renameWidget={renameWidget}
        duplicateWidget={duplicateWidget}
        removeWidget={removeWidget}
        moveWidgetToSection={moveWidgetToSection}
        onReset={resetUserSections}
        onExitEdit={() => setEditMode(false)}
      />
    );
  }

  return (
    <DashboardFilterProvider>
    <div className="w-full py-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-6 px-1">
        <h2 className="text-xl font-semibold text-primary flex-1 min-w-0 truncate">
          {config.name}
        </h2>
        <GlobalDateFilter />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2.5 rounded-md border border-border text-muted hover:text-primary hover:bg-surface-elevated transition-colors cursor-pointer"
              aria-label="Dashboard settings"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                closeSidePanel();
                setEditMode(true);
              }}
            >
              <Settings2 className="h-3.5 w-3.5" />
              Edit Layout
            </DropdownMenuItem>
            {hiddenCount > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => mutateOverride((o) => ({ ...o, hiddenInstanceIds: [] }))}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restore {hiddenCount} hidden widget{hiddenCount > 1 ? "s" : ""}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Sections */}
      <ActiveFilterBar />
      <div className="flex flex-col gap-6">
          {sortedSections.map((section) => {
            const isCollapsed = collapsedSections.has(section.id);
            const masterPlacements = section.placements;
            const overridePlacements = override.sectionLayouts[section.id];
            const basePlacements = overridePlacements ?? masterPlacements;
            const visiblePlacements = basePlacements.filter(
              (p) => !override.hiddenInstanceIds.includes(placementKey(p)),
            );
            const placementsWithHidden = masterPlacements.map((p) => ({
              ...p,
              hidden: override.hiddenInstanceIds.includes(placementKey(p)),
            }));

            return (
              <div key={section.id} className="flex flex-col">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <button
                    onClick={() => toggleCollapse(section.id)}
                    className="text-muted hover:text-primary transition-colors cursor-pointer"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  <span className="text-sm font-semibold text-primary">{section.name}</span>
                  {visiblePlacements.length < masterPlacements.length && (
                    <span className="text-xs text-muted">
                      {visiblePlacements.length}/{masterPlacements.length} widgets visible
                    </span>
                  )}
                </div>
                {!isCollapsed && (
                  <GridLayoutCore
                    placements={placementsWithHidden}
                    mode="view"
                    onLayoutChange={(updated) => updateSectionLayout(section.id, updated)}
                    onRemoveWidget={(instanceId) => toggleHideWidget(instanceId)}
                  />
                )}
              </div>
            );
          })}
        </div>
    </div>
    </DashboardFilterProvider>
  );
}
